<script>
import { mapGetters } from 'vuex';
import { useAlert, useTrack } from 'dashboard/composables';
import CsatMetrics from './components/CsatMetrics.vue';
import CsatTable from './components/CsatTable.vue';
import CsatFilters from './components/Csat/CsatFilters.vue';
import { generateFileName } from '../../../../helper/downloadHelper';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';

export default {
  name: 'CsatResponses',
  components: {
    CsatMetrics,
    CsatTable,
    CsatFilters,
    ReportHeader,
    V4Button,
  },
  data() {
    return {
      pageIndex: 0,
      from: 0,
      to: 0,
      userIds: [],
      inbox: null,
      team: null,
      rating: null,
      isDownloading: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledOnAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'csat/getUIFlags',
    }),
    hasLoadError() {
      return this.uiFlags.hasFetchError || this.uiFlags.hasMetricsError;
    },
    isFetching() {
      return this.uiFlags.isFetching || this.uiFlags.isFetchingMetrics;
    },
    requestPayload() {
      return {
        from: this.from,
        to: this.to,
        user_ids: this.userIds,
        inbox_id: this.inbox,
        team_id: this.team,
        rating: this.rating,
      };
    },
    isTeamsEnabled() {
      return this.isFeatureEnabledOnAccount(
        this.accountId,
        FEATURE_FLAGS.TEAM_MANAGEMENT
      );
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
  },
  methods: {
    getAllData() {
      this.$store.dispatch('csat/getMetrics', this.requestPayload);
      this.getResponses();
    },
    getResponses() {
      this.$store.dispatch('csat/get', {
        page: this.pageIndex + 1,
        ...this.requestPayload,
      });
    },
    async downloadReports() {
      if (this.isDownloading || this.isFetching || this.hasLoadError) return;
      const type = 'csat';
      this.isDownloading = true;
      try {
        await this.$store.dispatch('csat/downloadCSATReports', {
          fileName: generateFileName({ type, to: this.to }),
          ...this.requestPayload,
        });
      } catch {
        useAlert(this.$t('CSAT_REPORTS.DOWNLOAD_FAILED'));
      } finally {
        this.isDownloading = false;
      }
    },
    onPageNumberChange(pageIndex) {
      this.pageIndex = pageIndex;
      this.getResponses();
    },
    onFilterChange({
      from,
      to,
      selectedAgents,
      selectedInbox,
      selectedTeam,
      selectedRating,
    }) {
      // do not track filter change on initial load
      if (this.from !== 0 && this.to !== 0) {
        useTrack(REPORTS_EVENTS.FILTER_REPORT, {
          filterType: 'date',
          reportType: 'csat',
        });
      }

      this.from = from;
      this.to = to;
      this.userIds = selectedAgents.map(el => el.id);
      this.inbox = selectedInbox?.id;
      this.team = selectedTeam?.id;
      this.rating = selectedRating?.value;
      this.pageIndex = 0;

      this.getAllData();
    },
  },
};
</script>

<template>
  <ReportHeader :header-title="$t('CSAT_REPORTS.HEADER')">
    <V4Button
      :label="$t('CSAT_REPORTS.DOWNLOAD')"
      icon="i-ph-download-simple"
      size="sm"
      :is-loading="isDownloading"
      :disabled="isDownloading || isFetching || hasLoadError"
      @click="downloadReports"
    />
  </ReportHeader>

  <div class="flex flex-col gap-6">
    <CsatFilters
      :show-team-filter="isTeamsEnabled"
      @filter-change="onFilterChange"
    />
    <div
      v-if="hasLoadError"
      role="alert"
      class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-n-ruby-6 bg-n-ruby-2 p-4 text-sm text-n-ruby-11"
    >
      <span>{{ $t('REPORT.DATA_FETCHING_FAILED') }}</span>
      <V4Button
        :label="$t('CSAT_REPORTS.RETRY')"
        icon="i-lucide-refresh-cw"
        class="min-h-11"
        @click="hasLoadError && getAllData()"
      />
    </div>
    <template v-else>
      <CsatMetrics :filters="requestPayload" />
      <CsatTable :page-index="pageIndex" @page-change="onPageNumberChange" />
    </template>
  </div>
</template>
